import React, { createContext, useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
const url = new URL(window.location.href);
const finalUrl = `/api/`;

axios.defaults.withCredentials = true;

const initialState = {
  isAuthenticated: false,
  user: null,
  isGuest: false,
  email: '',
  password: '',
  rememberMe: false,
};

const AuthReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isGuest: action.payload.isGuest,
        email: action.payload.email,
        password: action.payload.password,
        rememberMe: action.payload.rememberMe,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        isGuest: false,
        email: '',
        password: '',
        rememberMe: false,
      };
    case 'SET_EMAIL':
      return {
        ...state,
        email: action.payload,
      };
    case 'SET_PASSWORD':
      return {
        ...state,
        password: action.payload,
      };
    case 'SET_REMEMBER_ME':
      return {
        ...state,
        rememberMe: action.payload,
      };
    default:
      return state;
  }
};

export const AuthContext = createContext(initialState);

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, initialState);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const response = await axios.get(finalUrl.concat('check-auth'));
        if (response.status === 200) {
          if (window.location.pathname === '/login') {
            navigate('/', { replace: true });
          }
          const { user } = response.data;
          const isGuest = user.isGuest || false;
          dispatch({
            type: 'LOGIN',
            payload: {
              user,
              isGuest,
              email: state.email,
              password: state.password,
              rememberMe: state.rememberMe,
            },
          });
        } else {
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        dispatch({ type: 'LOGOUT' });
      } finally {
        setLoading(false);
      }
    };
    checkAuthentication();
  }, []);

  const login = async ({ email, password, rememberMe }) => {
    try {
      const response = await axios.post(finalUrl.concat('login'), {
        username: email,
        password,
        rememberMe,
      });
      if (response.status === 200) {
        const { token, user } = response.data;
        const isGuest = user.isGuest || false;
        Cookies.set('token', token, {
          expires: rememberMe ? 7 : undefined,
          path: '/',
          sameSite: 'Strict',
          httpOnly: true,
          secure: true,
        });
        dispatch({
          type: 'LOGIN',
          payload: {
            user,
            isGuest,
            email,
            password,
            rememberMe,
          },
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const guestLogin = async () => {
    try {
      let user;
      try {
        const checkResponse = await axios.get(finalUrl.concat('check-auth'), {
          withCredentials: true,});
        if (checkResponse.status === 200 && checkResponse.data.user.isGuest) {
          user = checkResponse.data.user;
          dispatch({
            type: 'LOGIN',
            payload: {
              user,
              isGuest: true,
              email: '',
              password: '',
              rememberMe: false,
            },
          });
          return true;
        }
      } catch (checkError) {
        if (checkError.response) {
          const status = checkError.response.status;
          if (status === 401) {
            console.log(
              'No valid login detected. Starting the guest login process.',
            );
          } else {
            console.error(
              `An abnormal status code ${status} occurred during authentication check. Error message:`,
              checkError,
            );
            throw checkError;
          }
        } else if (checkError.request) {
          console.error(
            'No response was received from the server during authentication check. Error message: ',
            checkError,
          );
          throw checkError;
        } else {
          console.error(
            'An error occurred while setting up the authentication check request. Error message: ',
            checkError,
          );
          throw checkError;
        }
      }

      const response = await axios.post(finalUrl.concat('guest-login'));
      if (response.status === 200) {
        const { user } = response.data;
        dispatch({
          type: 'LOGIN',
          payload: {
            user,
            isGuest: true,
            email: '',
            password: '',
            rememberMe: false,
          },
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Guest login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      const response = await axios.get(finalUrl.concat('logout'));
      if (response.status === 200) {
        Cookies.remove('token', { path: '/' });
        dispatch({ type: 'LOGOUT' });
        navigate('/login');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        guestLogin,
        logout,
        loading,
        setEmail: (email) => dispatch({ type: 'SET_EMAIL', payload: email }),
        setPassword: (password) =>
          dispatch({ type: 'SET_PASSWORD', payload: password }),
        setRememberMe: (rememberMe) =>
          dispatch({ type: 'SET_REMEMBER_ME', payload: rememberMe }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
